import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../../api";
import type { Company } from "../../models";
import type { User } from "../../models";
import type { CompanyContextValue } from "./CompanyContext.types";
import { sortCompanies } from "../../domain/job/jobPrepService";

export function useCompanyContextViewModel(user: User | undefined): CompanyContextValue {
	const [companies, setCompanies] = useState<Company[]>([]);
	const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const setSelectedCompany = useCallback(
		(company: Company | null) => {
			setSelectedCompanyState(company);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (company) {
						next.set("company", company.id);
					} else {
						next.delete("company");
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	useEffect(() => {
		if (!user?.id) {
			return;
		}

		let cancelled = false;

		async function fetchData() {
			setIsLoading(true);
			try {
				const fetchedCompanies = await API.Data.Companies.get.byUser(user!);
				if (cancelled) return;

				const sorted = sortCompanies(fetchedCompanies);
				setCompanies(sorted);

				// Resolve initial selection from URL or auto-select
				const urlCompanyId = searchParams.get("company");
				if (urlCompanyId) {
					const match = sorted.find((c) => c.id === urlCompanyId);
					if (match) {
						setSelectedCompanyState(match);
					} else {
						// Invalid URL param, fall back to first
						const first = sorted[0] ?? null;
						setSelectedCompanyState(first);
						if (first) {
							setSearchParams(
								(prev) => {
									const next = new URLSearchParams(prev);
									next.set("company", first.id);
									return next;
								},
								{ replace: true },
							);
						}
					}
				} else if (sorted.length >= 1) {
					const first = sorted[0];
					setSelectedCompanyState(first);
					if (sorted.length > 1) {
						setSearchParams(
							(prev) => {
								const next = new URLSearchParams(prev);
								next.set("company", first.id);
								return next;
							},
							{ replace: true },
						);
					}
				}
			} catch {
				// Error handled silently; pages will show empty state
			} finally {
				if (!cancelled) {
					setIsLoading(false);
					setIsInitialized(true);
				}
			}
		}

		fetchData();

		return () => {
			cancelled = true;
		};
	}, [user?.id]);

	return {
		companies,
		selectedCompany,
		setSelectedCompany,
		isLoading,
		isInitialized,
	};
}
